import type { IRequestOpts, IResultstash, JsonQuery } from "@mne-core/api";
import { QueryController, createPostRequestOpts, createUrl, fetchJsonQuery } from "@mne-core/api";

import type { IAuthenticationData } from "../@types";

/**
 * Interface representing the criteria for SMS authentication.
 *
 * Requires SMS authentication to be enabled on the server. Use the `/methods` endpoint to check which
 * authentication methods are available on the server. The client can optionally request specific
 * authorizations as part of the authentication transaction, though these authorizations are not
 * guaranteed to be granted. The `setPassword` authorization, is only returned if
 * the current user has the `FORCE_PW_CHANGE` flag set.
 *
 *
 * @interface
 * @property {Array<"enrollPublicKey" | "setPassword">} [requestedAuthorizations] - Allow client to request authorizations as part of the authentication transaction. The client is not guaranteed to get the requested authorizations. The setPassword authorization is only returned if the current user has the FORCE_PW_CHANGE flag set.
 * @property {string} userName - Username to authenticate.
 * @property {string} domain - User domain.
 * @property {"PASSWORD" | "VERIFICATION"} mode - Use PASSWORD for initial request, and SMS to respond with SMS code.
 * @property {string} passcode - Password or SMS code.
 * @property {string} [cookie] - When responding with SMS code, set the SMS session cookie returned by challenge.
 */
export interface IAuthenticateSMSCriteria {
  requestedAuthorizations?: ["enrollPublicKey" | "setPassword"];
  userName: string;
  domain: "MNEMONIC" | "NFCERT";
  mode: "PASSWORD" | "VERIFICATION";
  passcode: string | number;
  cookie?: string;
}

export const createRequest = (criteria: IAuthenticateSMSCriteria, opts: IRequestOpts): Request => {
  const { apiUrl } = opts;
  const uri = createUrl(apiUrl, ["authentication", "v1", "sms", "authentication"]).toString();
  return new Request(uri, createPostRequestOpts(JSON.stringify(criteria), opts));
};

/**
 * Performs SMS authentication by submitting the specified criteria to the server.
 * This function supports a two-step authentication process. In the first step, with mode 'PASSWORD',
 * the function submits the username and static password. The server responds with a challenge and a session cookie.
 * In the second step, with mode 'VERIFICATION', it submits the username, the received SMS code as passcode,
 * and the session cookie. The function then awaits an authentication result.
 *
 * Requires SMS authentication to be enabled on the server and for the user. Use `/methods` to check which
 * authentication methods are available on the server.
 *
 * @async
 * @param {IAuthenticateSMSCriteria} criteria - The criteria for SMS authentication.
 * @param {IRequestOpts} opts - Additional request options, including API URL and controller.
 * @returns {Promise<JsonQuery<IResultstash<IAuthenticationData>>>} A promise resolving to the authentication result.
 */

export async function authenticateSMS(
  criteria: IAuthenticateSMSCriteria,
  opts: IRequestOpts,
): Promise<JsonQuery<IResultstash<IAuthenticationData>>> {
  return await fetchJsonQuery<IResultstash<IAuthenticationData>>(createRequest(criteria, opts), {
    ctrl: opts.ctrl || new QueryController(),
  });
}

export default authenticateSMS;
